import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
// plans are now fetched live from Supabase via usePricingPlans hook
export const plans = []; // kept for backward compat — use usePricingPlans() instead

const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Features', path: '/#features' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'Contact', path: '/contact' },
];

function BrandMark({ scrolled }) {
    return (
        <Link to="/" className="flex items-center gap-3 transition-colors duration-300" aria-label="GetYourQR home">
            <img
                src="/logo.png"
                alt="GetYourQR Logo"
                className={`object-contain transition-colors duration-300 ${scrolled ? 'w-10 h-10' : 'w-[52px] h-[52px]'}`}
            />
            <div className="leading-none flex flex-col justify-center">
                <span className={`font-black tracking-tight text-[#111827] transition-colors duration-300 ${scrolled ? 'text-[18px]' : 'text-[24px]'}`}>GetYourQR</span>
                {!scrolled && <span className="text-[11px] font-bold text-[#6B7280] tracking-wide mt-1">QR Ordering System</span>}
            </div>
        </Link>
    );
}

export default function MarketingLayout({ children }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    // Close mobile menu on route change
    useEffect(() => { setMenuOpen(false); }, [location]);

    // Add shadow on scroll
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <div className="marketing-page min-h-screen">
            {/* ── Navbar ─────────────────────────────────────── */}
            <header
                className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB] transition-shadow duration-300"
                style={{ boxShadow: scrolled ? '0 2px 16px rgba(0,0,0,0.07)' : 'none' }}
            >
                <div className="mx-auto flex max-w-[1100px] items-center justify-between gap-4 px-5 py-3.5">
                    <BrandMark scrolled={scrolled} />

                    {/* Desktop nav */}
                    <nav className="hidden items-center gap-1 md:flex">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                className={({ isActive }) =>
                                    `rounded-lg px-4 py-2 text-[14px] font-bold transition-colors ${isActive
                                        ? 'text-[#111827] bg-[#fef9c3]'
                                        : 'text-[#374151] hover:text-[#111827] hover:bg-[#F9FAFB]'
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    {/* Desktop CTAs */}
                    <div className="hidden items-center gap-3 md:flex">
                        <Link
                            to="/login"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '9px 18px',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#374151',
                                background: '#fff',
                                border: '1.5px solid #E5E7EB',
                                borderRadius: '8px',
                                textDecoration: 'none',
                                transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                            Login
                        </Link>
                        <Link to="/login" className="m-btn-primary" style={{ padding: '9px 18px', fontSize: '14px' }}>
                            Start Free Setup
                        </Link>
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        className="flex md:hidden items-center justify-center w-9 h-9 rounded-lg border border-[#E5E7EB] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                        onClick={() => setMenuOpen(o => !o)}
                        aria-label="Toggle menu"
                    >
                        {menuOpen ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                                <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Mobile drawer */}
                {menuOpen && (
                    <div className="md:hidden border-t border-[#E5E7EB] bg-white px-4 pb-4 pt-3 marketing-scale-in">
                        <nav className="flex flex-col gap-1 mb-4">
                            {navItems.map((item) => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    className={({ isActive }) =>
                                        `rounded-lg px-4 py-2.5 text-[14px] font-bold transition-colors ${isActive
                                            ? 'text-[#111827] bg-[#fef9c3]'
                                            : 'text-[#374151] hover:bg-[#F9FAFB]'
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                        </nav>
                        <div className="flex flex-col gap-2">
                            <Link to="/login" className="m-btn-primary justify-center" style={{ padding: '12px 20px', fontSize: '14px' }}>
                                Start Free Setup
                            </Link>
                            <Link
                                to="/login"
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    padding: '12px 20px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#374151',
                                    textDecoration: 'none',
                                    border: '1.5px solid #E5E7EB',
                                    borderRadius: '10px',
                                }}
                            >
                                Login
                            </Link>
                        </div>
                    </div>
                )}
            </header>

            {children}

            {/* ── Footer ─────────────────────────────────────── */}
            <footer style={{ borderTop: '1px solid #E5E7EB', background: '#fff' }}>
                <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:px-8">
                    {/* Brand */}
                    <div>
                        <BrandMark scrolled={false} />
                        <p style={{ marginTop: 16, fontSize: 14, color: '#6B7280', lineHeight: 1.7, maxWidth: 280 }}>
                            A fully functional hotel and restaurant management system. Modern SaaS for digital ordering and operations.
                        </p>
                        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: '#4B5563' }}>
                            <p><strong>Address:</strong> Sudama Nagar, Indore, Madhya Pradesh, 452009</p>
                            <p><strong>Email:</strong> <a href="mailto:contact@getyourqr.in" style={{ color: '#F59E0B', textDecoration: 'none' }}>contact@getyourqr.in</a></p>
                            <p><strong>Phone:</strong> <a href="https://wa.me/919770511893" target="_blank" rel="noopener noreferrer" style={{ color: '#10B981', textDecoration: 'none', fontWeight: 'bold' }}>+91 97705 11893 (WhatsApp)</a></p>
                        </div>
                    </div>

                    {/* Product */}
                    <div>
                        <p style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>Product</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {[
                                { label: 'Features', path: '/#features' },
                                { label: 'Pricing', path: '/pricing' },
                                { label: 'Live Demo', path: '/login' },
                                { label: 'Get Started', path: '/login' },
                            ].map(item => (
                                <Link key={item.label} to={item.path} style={{ fontSize: 14, fontWeight: 600, color: '#374151', textDecoration: 'none' }}
                                    onMouseEnter={e => e.currentTarget.style.color = '#F59E0B'}
                                    onMouseLeave={e => e.currentTarget.style.color = '#374151'}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Company */}
                    <div>
                        <p style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>Company</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {[
                                { label: 'About', path: '/about' },
                                { label: 'Contact', path: '/contact' },
                            ].map(item => (
                                <Link key={item.label} to={item.path} style={{ fontSize: 14, fontWeight: 600, color: '#374151', textDecoration: 'none' }}
                                    onMouseEnter={e => e.currentTarget.style.color = '#F59E0B'}
                                    onMouseLeave={e => e.currentTarget.style.color = '#374151'}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Legal */}
                    <div>
                        <p style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>Legal</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {[
                                { label: 'Privacy Policy', path: '/privacy' },
                                { label: 'Terms & Conditions', path: '/terms' },
                            ].map(item => (
                                <Link key={item.label} to={item.path} style={{ fontSize: 14, fontWeight: 600, color: '#374151', textDecoration: 'none' }}
                                    onMouseEnter={e => e.currentTarget.style.color = '#F59E0B'}
                                    onMouseLeave={e => e.currentTarget.style.color = '#374151'}
                                >
                                    {item.label}
                                </Link>
                            ))}
                            <Link to="/login" style={{ fontSize: 14, fontWeight: 700, color: '#F59E0B', textDecoration: 'none' }}>
                                Login →
                            </Link>
                        </div>
                    </div>
                </div>

                <div style={{ borderTop: '1px solid #E5E7EB', padding: '16px 24px', textAlign: 'center', fontSize: 13, color: '#9CA3AF', fontWeight: 500 }}>
                    © {new Date().getFullYear()} GetYourQR. Made for Indian restaurants. 🇮🇳
                </div>
            </footer>
        </div>
    );
}
